/* RevRunners: catalogue-backed cascading wheel specifications. No keyword search or cart writes. */
(function () {
  'use strict';
  const fields = ['diameter', 'width', 'pattern', 'offset'];
  const uniq = values => [...new Set(values)];
  const clean = value => String(value || '').replace(/[\u00d7\u2715]/g, 'x').replace(/[\u2212\u2013\u2014]/g, '-');
  function pcd(holes, value) {
    let n = Number(value);
    if (n >= 3 && n <= 10) n = Math.round(n * 25.4 * 100) / 100;
    return Number.isFinite(n) && n >= 75 && n <= 250 ? Number(holes) + 'x' + n : null;
  }
  function parseSpecs(title) {
    const text = clean(title), sizes = [], patterns = [], offsets = [], used = new Set();
    if (/\b(?:undrilled|unlimited|staggered)\b|custom\s+(?:offset|bolt|pcd)/i.test(text)) return null;
    for (const m of text.matchAll(/(?:^|[^\d.a-z])(\d{2}(?:\.\d+)?)\s*x\s*(\d{1,2}(?:\.\d+)?)(?![\d.])/gi)) {
      const d = Number(m[1]), w = Number(m[2]);
      if (d >= 13 && d <= 30 && w >= 3 && w <= 16) sizes.push(d + '|' + w);
    }
    if (uniq(sizes).length !== 1) return null;
    const [diameter, width] = sizes[0].split('|').map(Number);
    for (const m of text.matchAll(/(?:^|[^\d.a-z])([4-8])\s*x\s*(\d{1,3}(?:\.\d+)?)(?![\d.])/gi)) {
      const value = pcd(m[1], m[2]);
      if (value) patterns.push(value);
      const second = text.slice(m.index + m[0].length).match(/^\s*\/\s*(\d{2,3}(?:\.\d+)?)(?![\d.x])/i);
      if (second) { const value2 = pcd(m[1], second[1]); if (value2) patterns.push(value2); }
    }
    if (/\b(?:et|offset)\s*[:=]?\s*[+-]?\d+(?:\.\d+)?\s*(?:to|-)\s*\d/i.test(text)) return null;
    // Capture suffix labels first, so "35mm ET 70.5mm bore" means ET35, not ET70.5.
    for (const m of text.matchAll(/(?:^|[\s/,(])([+-]?\d{1,3}(?:\.\d+)?)\s*(?:mm\s*)?(offset|off|et)\b/gi)) {
      offsets.push(Number(m[1]));
      used.add(m.index + m[0].toLowerCase().lastIndexOf(m[2].toLowerCase()));
    }
    for (const m of text.matchAll(/\b(?:et|offset)\s*[:=]?\s*([+-]?\d{1,3}(?:\.\d+)?)(?![\d.])/gi)) {
      if (!used.has(m.index)) offsets.push(Number(m[1]));
    }
    if (!offsets.length) {
      for (const m of text.matchAll(/(?:^|[\s/(])([+-]\d{1,3}(?:\.\d+)?)(?:\s*mm)?(?=[\s/),]|$)/gi)) {
        if (!/^\s*(?:PFS|CB|bore|hub|BS|backspace|lip|PCD)\b/i.test(text.slice(m.index + m[0].length))) offsets.push(Number(m[1]));
      }
    }
    const valid = uniq(offsets.filter(n => Number.isFinite(n) && Math.abs(n) <= 150));
    return patterns.length && valid.length === 1 ? {diameter, width, patterns:uniq(patterns), offset:valid[0]} : null;
  }
  function compile(records) {
    const seen = new Set(), items = [];
    for (const row of records) {
      if (!row || typeof row.title !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(row.handle || '') || seen.has(row.handle)) continue;
      const spec = parseSpecs(row.title);
      if (spec) { seen.add(row.handle); items.push({handle:row.handle, title:row.title, ...spec}); }
    }
    return items;
  }
  function matching(items, selected, through = 4) {
    return items.filter(item => fields.slice(0, through).every((field, i) => selected[i] === '' || selected[i] == null ||
      (field === 'pattern' ? item.patterns.includes(String(selected[i])) : item[field] === Number(selected[i]))));
  }
  function optionsFor(items, selected, step) {
    const counts = new Map();
    for (const item of matching(items, selected, step)) {
      const values = step === 2 ? item.patterns : [String(item[fields[step]])];
      for (const value of values) counts.set(value, (counts.get(value) || 0) + 1);
    }
    return [...counts].sort((a, b) => {
      if (step !== 2) return Number(a[0]) - Number(b[0]);
      const aa = a[0].split('x').map(Number), bb = b[0].split('x').map(Number);
      return aa[0] - bb[0] || aa[1] - bb[1];
    });
  }
  const api = {parseSpecs, compile, matching, optionsFor};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('rr-wheel-cascade')) return;
  const catalogues = new Map();
  async function catalogue(url, expected) {
    if (!catalogues.has(url)) {
      const promise = (async () => {
        const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 45000);
        let text;
        try {
          const response = await fetch(url, {signal:controller.signal, credentials:'omit'});
          if (!response.ok) throw new Error('Catalogue unavailable');
          text = await response.text();
        } finally { clearTimeout(timer); }
        const records = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(line => line.trim()).map(line => JSON.parse(line));
        if (expected && records.length !== expected) throw new Error('Incomplete catalogue');
        const items = compile(records);
        if (!items.length) throw new Error('No readable specifications');
        return items;
      })();
      catalogues.set(url, promise);
      promise.catch(() => { if (catalogues.get(url) === promise) catalogues.delete(url); });
    }
    return catalogues.get(url);
  }
  class RRWheelCascade extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true; this.serial = 0; this.selected = ['', '', '', ''];
      this.form = this.querySelector('form'); this.selects = [...this.form.querySelectorAll('select')];
      this.status = this.querySelector('[data-status]'); this.results = this.querySelector('[data-results]');
      this.submit = this.querySelector('[data-submit]'); this.more = this.querySelector('[data-more]'); this.retry = this.querySelector('[data-retry]');
      this.events = new AbortController(); const eventOptions = {signal:this.events.signal};
      this.form.addEventListener('submit', e => { e.preventDefault(); this.search(); }, eventOptions);
      this.selects.forEach((select, step) => select.addEventListener('change', () => this.change(step), eventOptions));
      this.querySelector('[data-reset]').addEventListener('click', () => this.reset(), eventOptions);
      this.more.addEventListener('click', () => this.showMore(), eventOptions);
      this.retry.addEventListener('click', () => this.load(), eventOptions);
      this.addEventListener('pointerdown', () => this.load(), eventOptions);
      this.addEventListener('focusin', () => this.load(), eventOptions);
      window.addEventListener('hashchange', () => this.openFromHash(), eventOptions);
      if (this.items) this.reset();
      this.openFromHash();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { this.observer.disconnect(); this.load(); } }, {rootMargin:'120px'});
        this.observer.observe(this);
      } else this.load();
    }
    disconnectedCallback() { this.events?.abort(); this.productController?.abort(); this.observer?.disconnect(); this.serial++; this.bound = false; }
    openFromHash() { if (location.hash === '#rr-wheel-finder') { this.querySelector('details').open = true; this.load(); } }
    async load() {
      if (this.items || this.loading) return;
      this.loading = true; this.retry.hidden = true; this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading wheel specifications...';
      try {
        this.items = await catalogue(this.dataset.catalogueUrl, Number(this.dataset.catalogueCount));
        if (!this.isConnected) return;
        this.reset();
      } catch (error) {
        if (!this.isConnected) return;
        this.status.textContent = 'Wheel specifications could not load. Please retry, or browse all wheels.';
        this.retry.hidden = false;
      } finally { this.loading = false; this.form.removeAttribute('aria-busy'); }
    }
    populate(step) {
      const select = this.selects[step];
      const unlocked = !!this.items && (step === 0 || this.selected[step - 1] !== '');
      const labels = ['Choose diameter', 'Choose width', 'Choose bolt pattern', 'Choose offset'];
      const locked = ['Loading sizes...', 'Choose diameter first', 'Choose width first', 'Choose bolt pattern first'];
      select.replaceChildren(new Option(unlocked ? labels[step] : locked[step], ''));
      if (unlocked) for (const [value, count] of optionsFor(this.items, this.selected, step)) {
        const label = step < 2 ? value + '\u2033' : step === 2 ? value.replace('x', ' x ') : (Number(value) > 0 ? '+' : '') + value + ' mm';
        select.add(new Option(label + ' (' + count + ')', value));
      }
      select.disabled = !unlocked;
      select.value = this.selected[step];
    }
    clearResults() {
      this.serial++; this.productController?.abort(); this.results.replaceChildren(); this.more.hidden = true;
      this.pool = []; this.shown = 0;
    }
    reset() {
      this.selected = ['', '', '', '']; this.clearResults();
      for (let i = 0; i < 4; i++) this.populate(i);
      this.submit.disabled = true;
      if (this.items) this.status.textContent = '';
    }
    change(step) {
      this.selected[step] = this.selects[step].value;
      for (let i = step + 1; i < 4; i++) this.selected[i] = '';
      this.clearResults();
      for (let i = step + 1; i < 4; i++) this.populate(i);
      const complete = this.selected.every(value => value !== '');
      this.submit.disabled = !complete;
      this.status.textContent = '';
    }
    search() {
      if (!this.items || !this.selected.every(value => value !== '') || !this.form.reportValidity()) return;
      this.clearResults(); this.pool = matching(this.items, this.selected);
      this.productController = new AbortController(); this.showMore();
    }
    showMore() {
      const batch = this.pool.slice(this.shown, this.shown + 12), serial = this.serial;
      const jobs = [];
      for (const item of batch) {
        const card = document.createElement('a'); card.className = 'rrw__result';
        card.href = this.dataset.root + 'products/' + encodeURIComponent(item.handle);
        const imageSlot = document.createElement('div'); imageSlot.className = 'rrw__image-slot';
        const body = document.createElement('div'); body.className = 'rrw__result-body';
        const title = document.createElement('h3'); title.textContent = item.title;
        const dims = document.createElement('p'); dims.className = 'rrw__dimensions';
        dims.textContent = item.diameter + ' x ' + item.width + ' in | ' + this.selected[2].replace('x', ' x ') + ' | ET ' + (item.offset > 0 ? '+' : '') + item.offset;
        const price = document.createElement('p'); price.className = 'rrw__price'; price.textContent = 'Check current price';
        const action = document.createElement('span'); action.className = 'rrw__view'; action.textContent = 'View wheel and availability \u2192';
        body.append(title, dims, price, action); card.append(imageSlot, body); this.results.append(card);
        jobs.push({item, card, imageSlot, title, price});
      }
      this.shown += batch.length; this.more.hidden = this.shown >= this.pool.length;
      this.status.textContent = 'Showing ' + this.shown + ' of ' + this.pool.length.toLocaleString() + ' matching catalogue listings. Prices and availability are checked on the product page.';
      // Limit concurrent product requests; failures never erase valid index results.
      let cursor = 0;
      const worker = async () => { while (cursor < jobs.length && serial === this.serial) await this.hydrate(jobs[cursor++], serial); };
      for (let i = 0; i < Math.min(3, jobs.length); i++) worker();
    }
    async hydrate(job, serial) {
      try {
        const signal = AbortSignal.any([this.productController.signal, AbortSignal.timeout(12000)]);
        const response = await fetch(this.dataset.root + 'products/' + encodeURIComponent(job.item.handle) + '.js', {credentials:'same-origin', signal});
        if (serial !== this.serial) return;
        if (response.status === 404) { job.card.remove(); return; }
        if (!response.ok) return;
        const product = await response.json();
        if (serial !== this.serial || !job.card.isConnected) return;
        const current = parseSpecs(product.title);
        if (product.handle !== job.item.handle || !current || !Array.isArray(product.variants) || product.variants.length !== 1 || !matching([{...current}], this.selected).length) {
          job.card.remove(); return;
        }
        job.title.textContent = product.title;
        const currency = window.Shopify?.currency?.active || this.dataset.currency;
        if (Number.isFinite(product.price) && /^[A-Z]{3}$/.test(currency)) {
          job.price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency, currencyDisplay:'code'}).format(product.price / 100);
        }
        const src = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src;
        if (src) {
          const url = new URL(src, location.origin);
          if (url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com')) {
            url.searchParams.set('width', '420');
            const img = document.createElement('img'); img.src = url.href; img.alt = ''; img.width = 320; img.height = 230; img.loading = 'lazy';
            job.imageSlot.append(img);
          }
        }
      } catch (error) { /* The direct product link remains usable if enrichment fails. */ }
    }
  }
  customElements.define('rr-wheel-cascade', RRWheelCascade);
})();
